import { PlanGroupSegment } from "../../admin-api/generated-src";

/**
 * Parses the UTC offset (in minutes) from the trailing timezone designator of an ISO-8601
 * string, e.g. "2026-07-12T07:30:00-04:00" → -240, "…Z"/"+00:00" → 0.
 *
 * Returns null when no offset can be determined (missing/naive string) — callers then fall
 * back to UTC rendering, preserving the previous behavior.
 */
export function offsetMinutesFromIso(iso?: string | null): number | null {
  if (!iso) {
    return null;
  }
  if (/[zZ]$/.test(iso)) {
    return 0;
  }
  const match = iso.match(/([+-])(\d{2}):(\d{2})$/);
  if (!match) {
    return null;
  }
  const sign = match[1] === "-" ? -1 : 1;
  const hours = Number(match[2]);
  const minutes = Number(match[3]);
  return sign * (hours * 60 + minutes);
}

/**
 * Epoch seconds → "HH:MM". When offsetMinutes is provided the time is rendered in that
 * UTC offset (e.g. the facility timezone); when null it renders in UTC, matching the
 * page's legacy behavior.
 */
function formatEpochToHHMM(epochSeconds: number, offsetMinutes: number | null): string {
  const shiftedSeconds = offsetMinutes == null ? epochSeconds : epochSeconds + offsetMinutes * 60;
  return new Date(shiftedSeconds * 1000).toISOString().substring(11, 16);
}

/** "HH:MM–HH:MM" from epoch start/end; "" when either is missing. */
export function formatSegmentTimeRange(
  startEpoch?: number | null,
  endEpoch?: number | null,
  offsetMinutes: number | null = null,
): string {
  if (startEpoch == null || endEpoch == null) {
    return "";
  }
  return `${formatEpochToHHMM(startEpoch, offsetMinutes)}–${formatEpochToHHMM(endEpoch, offsetMinutes)}`;
}

/** Label for a segment; falls back to the raw segmentId when epochs are absent. */
export function formatSegment(
  seg: Pick<PlanGroupSegment, "segmentId" | "segmentStart" | "segmentEnd">,
  offsetMinutes: number | null = null,
): string {
  return formatSegmentTimeRange(seg.segmentStart, seg.segmentEnd, offsetMinutes) || seg.segmentId || "";
}
